import { PageHeader } from "@/components/page-header";
import { Footer } from "@/components/footer";
import { Button } from "@/components/ui/button";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

export const Route = createFileRoute("/_marketing/features")({ component: FeaturesPage });

const workflows = [
  {
    id: "families",
    label: "Families & students",
    title: "Keep family and student records together.",
    description:
      "See the adults and children connected to each family and keep their information current as your community changes.",
    details: [
      "Add and manage parents on family records",
      "Review and merge duplicate member profiles",
      "Export student information for your program’s records",
    ],
    image: "/features-family.jpg",
    width: 975,
    height: 300,
    alt: "Sample family record showing two adult members",
  },
  {
    id: "enrollment",
    label: "Applications & enrollment",
    title: "Follow families from application to class choices.",
    description:
      "Review applications and give parents a place to manage their students’ class enrollment for the term.",
    details: [
      "Approve or reject family applications",
      "Let parents select classes and see enrollment and waitlist status",
      "See how enrollment changes affect tuition",
    ],
    image: "/features-enrollment.jpg",
    width: 975,
    height: 288,
    alt: "Sample parent enrollment showing a student’s enrolled class and a closed class day",
  },
  {
    id: "classes",
    label: "Classes & schedules",
    title: "Build a term your families can follow.",
    description:
      "Set up the class days and classes that make up your semester, then give families a view of their students’ schedules.",
    details: [
      "Manage class days within semester settings",
      "Copy classes into a new semester",
      "Review class rosters and student schedules",
    ],
    image: "/features-classes.jpg",
    width: 975,
    height: 265,
    alt: "Sample class list showing teachers, class days, and enrollment counts",
  },
  {
    id: "payments",
    label: "Fees & payments",
    title: "See what each family owes and has paid.",
    description:
      "Use your program’s fee structures to track tuition and fees alongside payments recorded for each family.",
    details: [
      "Set fee structures for the semester",
      "Review family amounts due, recorded payments, and balances",
      "Give parents a view of their tuition and payment information",
    ],
    image: "/features-payments.jpg",
    width: 755,
    height: 130,
    alt: "Sample family tuition summary with total due, total paid, and remaining balance",
  },
  {
    id: "updates",
    label: "Teacher updates",
    title: "Share class information with the right families.",
    description:
      "Teachers can prepare updates for their classes, and parents can return to those updates when they need the details.",
    details: [
      "Draft, publish, and update class messages with attachments",
      "Parents can search and filter their class updates",
      "Send class updates to a student’s email address after a parent verifies it",
    ],
    image: "/features-updates.jpg",
    width: 975,
    height: 425,
    alt: "Sample parent feed showing updates from enrolled classes",
  },
  {
    id: "access",
    label: "Access & organization site",
    title: "Give each person a useful view of the program.",
    description:
      "Administrators, teachers, and parents each have places to work with the information relevant to their role.",
    details: [
      "Administrators manage families, classes, and enrollment",
      "Teachers view class rosters and share class updates",
      "Parents see their students’ enrollment, schedules, and updates",
      "Share a branded organization homepage at your Veronics site address",
    ],
  },
];

function FeaturesPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <main className="flex-1">
        <PageHeader
          title="Features for your learning community"
          description="See how Veronics supports families, enrollment, classes, and tuition throughout the term."
        />

        <div className="container py-12 md:py-16">
          <nav id="workflows" aria-label="On this page" className="mb-8 scroll-mt-24 border-b border-border pb-8">
            <p className="text-sm font-semibold text-foreground">Explore the workflows</p>
            <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-3 text-sm">
              {workflows.map(({ id, label }) => (
                <li key={id}>
                  <a
                    href={`#${id}`}
                    className="font-medium text-primary underline-offset-4 hover:underline dark:text-foreground"
                  >
                    {label}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="#in-development"
                  className="font-medium text-primary underline-offset-4 hover:underline dark:text-foreground"
                >
                  In development
                </a>
              </li>
            </ul>
          </nav>
          {workflows.map(({ id, title, description, details, image, alt, width, height }, index) => (
            <section
              id={id}
              key={id}
              className={`scroll-mt-24 rounded-xl px-6 py-12 md:px-10 md:py-16 ${index % 2 === 0 ? "bg-muted/40 dark:bg-card/40" : "bg-background"}`}
            >
              <div className="space-y-8">
                <div className="max-w-3xl">
                  <h2 className="text-3xl font-semibold leading-tight text-primary dark:text-foreground md:text-4xl">
                    {title}
                  </h2>
                  <p className="mt-4 max-w-2xl text-base leading-7 text-foreground/85">{description}</p>
                  <ul className="mt-5 list-disc space-y-2 pl-5 text-base leading-7 text-foreground/85">
                    {details.map((detail) => (
                      <li key={detail}>{detail}</li>
                    ))}
                  </ul>
                </div>
                {image && (
                  <figure className="max-w-4xl">
                    <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
                      <img src={image} alt={alt} width={width} height={height} loading="lazy" className="w-full" />
                    </div>
                  </figure>
                )}
              </div>
            </section>
          ))}
          <section
            id="in-development"
            className="scroll-mt-24 rounded-xl bg-muted/40 px-6 py-12 dark:bg-card/40 md:px-10 md:py-16"
          >
            <h2 className="text-2xl font-semibold text-primary dark:text-foreground md:text-3xl">In development</h2>
            <p className="mt-3 max-w-2xl leading-7 text-foreground/85">
              These features are in development and are not yet available.
            </p>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div className="rounded-xl border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">Organization forms</h3>
                <p className="mt-2 leading-7 text-muted-foreground">
                  Create and publish forms for families to complete, and review submitted responses.
                </p>
              </div>
              <div className="rounded-xl border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">Additional organization website pages</h3>
                <p className="mt-2 leading-7 text-muted-foreground">
                  Create and publish additional pages on your organization’s Veronics site.
                </p>
              </div>
            </div>
          </section>
          <section className="mt-12 rounded-xl bg-primary p-8 text-primary-foreground md:p-10">
            <h2 className="text-3xl font-semibold text-primary-foreground">Tell us how your program works.</h2>
            <p className="mt-3 max-w-2xl leading-7 text-primary-foreground/85">
              We’d be glad to learn about your enrollment, scheduling, and administrative needs.
            </p>
            <Button
              asChild
              size="lg"
              className="mt-6 max-w-full bg-accent px-7 font-semibold text-accent-foreground shadow-lg hover:bg-terracotta-dark"
            >
              <Link to="/contact-us">
                Contact us <ArrowRight aria-hidden="true" className="ml-2 size-4" />
              </Link>
            </Button>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
